import { useState, useCallback, useMemo, useEffect } from "react";
import { Cafe } from "../../../../api/types";

interface AreaFilter {
  name: string;
  value: number[] | null;
}

interface UseFranchiseFiltersReturn {
  // 필터 상태
  search: string;
  selectedArea: AreaFilter;
  isMyPub: boolean;

  // 필터 설정 함수
  setSearch: (search: string) => void;
  setSelectedArea: (area: AreaFilter) => void;
  setIsMyPub: (isMyPub: boolean) => void;
  handleSearch: () => void;
  clearAllFilters: () => void;

  // 필터링된 결과
  filteredList: Cafe[];

  // 필터 상태 정보
  hasActiveFilters: boolean;
  filterCount: number;
}

export const useFranchiseFilters = (
  allList: Cafe[]
): UseFranchiseFiltersReturn => {
  // 필터 상태
  const [search, setSearch] = useState<string>("");
  const [selectedArea, setSelectedArea] = useState<AreaFilter>({
    name: "전체",
    value: null,
  });
  const [isMyPub, setIsMyPub] = useState<boolean>(false);

  // 디바운스된 검색어 (성능 개선)
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");

  // 검색어 디바운스 처리
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300); // 300ms 디바운스

    return () => clearTimeout(timer);
  }, [search]);

  // 필터링 로직 개선
  const filteredList = useMemo(() => {
    let result = [...allList];

    // 1. 검색 필터 (디바운스된 검색어 사용)
    if (debouncedSearch.trim()) {
      const searchTerm = debouncedSearch.toLowerCase().trim();
      result = result.filter(
        (cafe) =>
          cafe.cafeName.toLowerCase().includes(searchTerm) ||
          cafe.newAddress?.toLowerCase().includes(searchTerm) ||
          cafe.areaProvince?.toLowerCase().includes(searchTerm) ||
          cafe.areaCity?.toLowerCase().includes(searchTerm)
      );
    }

    // 2. 지역 필터
    if (selectedArea.name !== "전체" && selectedArea.value?.length) {
      result = result.filter((cafe) =>
        selectedArea.value!.includes(cafe.areaProvinceId)
      );
    }

    // 3. 내 펍 필터 (즐겨찾기)
    if (isMyPub) {
      result = result.filter((cafe) => cafe.like);
    }

    return result;
  }, [allList, debouncedSearch, selectedArea, isMyPub]);

  // 검색 실행 함수 (엔터키나 검색 버튼 클릭 시)
  const handleSearch = useCallback(() => {
    setDebouncedSearch(search);
  }, [search]);

  // 모든 필터 초기화
  const clearAllFilters = useCallback(() => {
    setSearch("");
    setSelectedArea({ name: "전체", value: null });
    setIsMyPub(false);
    setDebouncedSearch("");
  }, []);

  // 활성 필터 여부 확인
  const hasActiveFilters = useMemo(() => {
    return (
      debouncedSearch.trim() !== "" || selectedArea.name !== "전체" || isMyPub
    );
  }, [debouncedSearch, selectedArea.name, isMyPub]);

  // 활성 필터 개수
  const filterCount = useMemo(() => {
    let count = 0;
    if (debouncedSearch.trim()) count++;
    if (selectedArea.name !== "전체") count++;
    if (isMyPub) count++;
    return count;
  }, [debouncedSearch, selectedArea.name, isMyPub]);

  return {
    // 필터 상태
    search,
    selectedArea,
    isMyPub,

    // 필터 설정 함수
    setSearch,
    setSelectedArea,
    setIsMyPub,
    handleSearch,
    clearAllFilters,

    // 필터링된 결과
    filteredList,

    // 필터 상태 정보
    hasActiveFilters,
    filterCount,
  };
};
